import m from 'mithril';
import app from 'flarum/forum/app';
import { override } from 'flarum/common/extend';
import ItemList from 'flarum/common/utils/ItemList';
import listItems from 'flarum/common/helpers/listItems';
import humanTime from 'flarum/common/helpers/humanTime';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import avatar from 'flarum/common/helpers/avatar';
import LogInModal from 'flarum/forum/components/LogInModal';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';
import WelcomeHero from 'flarum/forum/components/WelcomeHero';
import IndexPage from 'flarum/forum/components/IndexPage';
import tagIcon from 'flarum/tags/helpers/tagIcon';
import { animeAvatarUrl } from './animeAvatar';
import { rankOfAttrs } from './rank';

const WA_URL = 'https://chat.whatsapp.com/CHKul7GrWr62ueU1B7k232';
const GITHUB_URL = 'https://github.com/ReverserID';

// Cache async per sumber, biar fetch cuma sekali dan render tetap pure.
const sectionCache = {}; // slug -> { state, discussions }
let railFiles = { state: 'loading', items: [] };
let railRes = { state: 'loading', items: [] };
let railOnline = { state: 'loading', items: [] };
let latestFeed = { state: 'loading', items: [] };

const startDiscussion = (e) => {
  if (e) e.preventDefault();
  const user = app.session.user;
  if (user) {
    app.composer.load(DiscussionComposer, { user });
    app.composer.show();
  } else {
    app.modal.show(LogInModal);
  }
};

const onEnter = (e) => {
  if (e.key === 'Enter' || e.key === ' ') startDiscussion(e);
};

function forumTags() {
  try {
    return app.store.all('tags') || [];
  } catch (_) {
    return [];
  }
}

function tagDiscussions(tag) {
  try {
    return tag.discussionCount() || 0;
  } catch (_) {
    return 0;
  }
}

function isValidTag(t) {
  try {
    return !!t && typeof t.slug === 'function' && !!t.slug();
  } catch (_) {
    return false;
  }
}

function tagColor(t) {
  try {
    return t.color() || '#8b93a3';
  } catch (_) {
    return '#8b93a3';
  }
}

function tagName(t) {
  try {
    return t.name() || '?';
  } catch (_) {
    return '?';
  }
}

function tagDot(t) {
  try {
    return t.icon() ? tagIcon(t, {}, { useColor: false }) : tagName(t).trim().charAt(0).toUpperCase();
  } catch (_) {
    return '?';
  }
}

function tagHref(t) {
  try {
    return app.route.tag(t);
  } catch (_) {
    return '#';
  }
}

function discussionHref(d) {
  try {
    return app.route.discussion(d);
  } catch (_) {
    return '#';
  }
}

function sectionTags() {
  return forumTags()
    .filter((t) => {
      try {
        if (!isValidTag(t)) return false;
        if (t.position() === null || t.position() === undefined) return false;
        return tagDiscussions(t) > 0;
      } catch (_) {
        return false;
      }
    })
    .sort((a, b) => {
      try {
        return tagDiscussions(b) - tagDiscussions(a);
      } catch (_) {
        return 0;
      }
    })
    .slice(0, 4);
}

// Fetch 4 diskusi terbaru per tag (sekali, via cache).
function loadSection(slug) {
  if (sectionCache[slug]) return;
  sectionCache[slug] = { state: 'loading', discussions: [] };
  app.store
    .find('discussions', { filter: { q: 'tag:' + slug }, page: { limit: 3 }, include: 'user,lastPostedUser,firstPost' })
    .then((col) => {
      sectionCache[slug] = { state: 'done', discussions: col.filter((d) => d && d.title).slice(0, 3) };
      m.redraw();
    })
    .catch(() => {
      sectionCache[slug] = { state: 'error', discussions: [] };
      m.redraw();
    });
}

function sectionRow(d) {
  let author = '';
  try {
    const u = d.user && d.user();
    author = u ? u.displayName() || u.username() : '';
  } catch (_) {
    author = '';
  }
  let when = null;
  try {
    when = d.lastPostedAt && d.lastPostedAt();
  } catch (_) {
    when = null;
  }
  let replies = 0;
  try {
    replies = d.commentCount ? d.commentCount() - 1 : 0;
    if (replies < 0) replies = 0;
  } catch (_) {
    replies = 0;
  }
  let title = '';
  try {
    title = d.title();
  } catch (_) {
    title = '';
  }
  let thumb = null;
  try {
    thumb = d.attribute && d.attribute('customThumbnail');
  } catch (_) {
    thumb = null;
  }
  let excerpt = '';
  try {
    const fp = d.firstPost && d.firstPost();
    const html = fp && fp.contentHtml ? fp.contentHtml() : null;
    if (html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      excerpt = (tmp.textContent || '').replace(/\s+/g, ' ').trim();
      if (excerpt.length > 150) excerpt = excerpt.slice(0, 150).trim() + '…';
    }
  } catch (_) {
    excerpt = '';
  }
  return (
    <Link className="RvsrSection-row" href={discussionHref(d)}>
      {thumb ? (
        <span className="RvsrSection-thumb">
          <img src={thumb} alt="" loading="lazy" />
        </span>
      ) : (
        <span className="RvsrSection-thumb RvsrSection-thumb--logo" aria-hidden="true">
          <i className="icon fas fa-microchip" />
        </span>
      )}
      <span className="RvsrSection-rowMain">
        <span className="RvsrSection-rowTitle">{title}</span>
        {excerpt ? <span className="RvsrSection-excerpt">{excerpt}</span> : null}
        <span className="RvsrSection-rowMeta">
          {author ? <span className="RvsrSection-author">{author}</span> : null}
          {when ? <span>{humanTime(when)}</span> : null}
          <span className="RvsrSection-replies">{replies} replies</span>
        </span>
      </span>
    </Link>
  );
}

// Render pure (bukan komponen) — hindari ambiguitas attrs/key Mithril.
function sectionView(tag) {
  if (!isValidTag(tag)) return null;
  const slug = tag.slug();
  loadSection(slug);
  const entry = sectionCache[slug] || { state: 'loading', discussions: [] };
  const href = tagHref(tag);
  return (
    <section className="RvsrSection" style={{ '--rv-tag': tagColor(tag) }}>
      <div className="RvsrSection-head">
        <Link className="RvsrSection-title" href={href}>
          <span className="RvsrSection-dot">{tagDot(tag)}</span>
          {tagName(tag)}
          <em className="RvsrSection-count">{tagDiscussions(tag)}</em>
        </Link>
        <Link className="RvsrSection-all" href={href}>
          See all →
        </Link>
      </div>
      {entry.state === 'loading' ? (
        <div className="RvsrSection-list">
          {[0, 1, 2].map(() => (
            <div className="RvsrSection-row is-loading">
              <span className="RvsrSection-skel" />
            </div>
          ))}
        </div>
      ) : entry.discussions.length ? (
        <div className="RvsrSection-list">{entry.discussions.map((d) => sectionRow(d))}</div>
      ) : (
        <div className="RvsrSection-empty">No discussions yet.</div>
      )}
    </section>
  );
}

// ------------------------------------------------------------ rail widgets
function fileIcon(name) {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) return 'fas fa-file-image';
  if (ext === 'pdf') return 'fas fa-file-pdf';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'fas fa-file-archive';
  if (['txt', 'md', 'log', 'nfo', 'diz'].includes(ext)) return 'fas fa-file-alt';
  if (['js', 'py', 'php', 'c', 'cpp', 'h', 'java', 'cs', 'go', 'rs', 'apk', 'exe', 'dll', 'so'].includes(ext)) return 'fas fa-file-code';
  if (['mp4', 'mkv', 'avi', 'mov'].includes(ext)) return 'fas fa-file-video';
  if (['mp3', 'wav', 'flac', 'ogg'].includes(ext)) return 'fas fa-file-audio';
  return 'fas fa-file';
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' });
  } catch (_) {
    return '';
  }
}

let railLoaded = false;
function loadRail() {
  if (railLoaded) return;
  railLoaded = true;
  app
    .request({ method: 'GET', url: app.forum.attribute('apiUrl') + '/fof/upload/shared-files', params: { 'page[limit]': 6 } })
    .then((res) => {
      const data = (res && res.data) || [];
      railFiles = { state: 'done', items: data.slice(0, 6).map((f) => ({ id: f.id, ...(f.attributes || {}) })) };
      m.redraw();
    })
    .catch(() => {
      railFiles = { state: 'error', items: [] };
      m.redraw();
    });
  app.store
    .find('discussions', { filter: { q: 'tag:resource' }, page: { limit: 5 }, include: 'user' })
    .then((col) => {
      railRes = { state: 'done', items: col.filter((d) => d && d.title).slice(0, 5) };
      m.redraw();
    })
    .catch(() => {
      railRes = { state: 'error', items: [] };
      m.redraw();
    });
  loadOnline();
  // refresh daftar online tiap 60 dtk (presence berbasis lastSeenAt)
  if (!loadRail._poll) {
    loadRail._poll = setInterval(() => {
      if (app.current && app.current.matches && app.current.matches(IndexPage)) loadOnline();
    }, 60000);
  }
}

function mapOnlineRows(data) {
  const now = Date.now();
  return (data || [])
    .map((u) => {
      try {
        const a = u.attributes || {};
        const ts = a.lastSeenAt ? new Date(a.lastSeenAt).getTime() : 0;
        const rel = u.relationships || {};
        let rank = null;
        try {
          rank = rankOfAttrs(a, {
            badges: rel.badges && rel.badges.data,
            groups: rel.groups && rel.groups.data,
          });
        } catch (_) {
          rank = null;
        }
        return {
          id: u.id,
          name: a.displayName || a.username,
          avatarUrl: a.avatarUrl,
          slug: a.slug,
          ts,
          online: ts > 0 && now - ts < 5 * 60 * 1000,
          rank,
        };
      } catch (_) {
        return null;
      }
    })
    .filter((u) => u && u.ts > 0 && u.name)
    .sort((x, y) => y.ts - x.ts)
    .slice(0, 12);
}

// Daftar user teraktif. Coba sort server (butuh izin user.viewLastSeenAt utk
// member); kalau ditolak (guest), fallback ambil apa adanya lalu urut sendiri.
function loadOnline() {
  const base = app.forum.attribute('apiUrl') + '/users';
  // Catatan penting (hasil tes API):
  //  - include=badges SELALU 400 invalid_parameter (bukan relasi yang bisa di-include).
  //  - sort=-lastSeenAt 400 utk actor tanpa izin user.viewLastSeenAt (mis. guest).
  //  - include=groups OK. Jadi: coba sorted+groups, fallback unsorted+groups,
  //    terakhir tanpa include. Rank tetap akurat dari commentCount + groups.
  const withGroups = { 'page[limit]': 24, include: 'groups' };
  const done = (res) => {
    railOnline = { state: 'done', items: mapOnlineRows(res && res.data) };
    m.redraw();
  };
  const fail = () => {
    railOnline = { state: 'error', items: [] };
    m.redraw();
  };
  app
    .request({ method: 'GET', url: base, params: { sort: '-lastSeenAt', ...withGroups } })
    .then(done)
    .catch(() => {
      app
        .request({ method: 'GET', url: base, params: withGroups })
        .then(done)
        .catch(() => {
          app.request({ method: 'GET', url: base, params: { 'page[limit]': 50 } }).then(done).catch(fail);
        });
    });
}

let latestLoaded = false;
function loadLatest() {
  if (latestLoaded) return;
  latestLoaded = true;
  app.store
    .find('discussions', { page: { limit: 6 }, include: 'user,lastPostedUser,firstPost' })
    .then((col) => {
      latestFeed = { state: 'done', items: (col || []).filter((d) => d && d.title).slice(0, 6) };
      m.redraw();
    })
    .catch(() => {
      latestFeed = { state: 'error', items: [] };
      m.redraw();
    });
}

// Section "Latest Posts" di kolom tengah (diskusi teraktif terbaru).
function latestSectionView() {
  loadLatest();
  // app.route('index') = '/' — link balik ke halaman yang sama (no-op).
  // Pakai /?sort=newest + gate di contentItems biar Flarum nampilin
  // discussion list asli (sort/pagination jalan).
  const allHref = '/?sort=newest';
  return (
    <section className="RvsrSection RvsrSection--latest">
      <div className="RvsrSection-head">
        <span className="RvsrSection-title">
          <span className="RvsrSection-dot RvsrSection-dot--latest">
            <i className="icon fas fa-bolt" />
          </span>
          Latest Posts
        </span>
        {/* anchor biasa: query-only change gak ke-detect mithril router,
            paksa full navigation biar ?sort=newest kebaca */}
        <a className="RvsrSection-all" href={allHref}>
          See all →
        </a>
      </div>
      {latestFeed.state === 'loading' ? (
        <div className="RvsrSection-list">
          {[0, 1, 2, 3].map(() => (
            <div className="RvsrSection-row is-loading">
              <span className="RvsrSection-skel" />
            </div>
          ))}
        </div>
      ) : latestFeed.items.length ? (
        <div className="RvsrSection-list">{latestFeed.items.map((d) => sectionRow(d))}</div>
      ) : (
        <div className="RvsrSection-empty">No posts yet.</div>
      )}
    </section>
  );
}

function railView() {
  loadRail();
  return (
    <div className="RvsrRail-in">
      <section className="RvsrWidget">
        <h3 className="RvsrWidget-title">Community</h3>
        <div className="RvsrLinks">
          <a className="RvsrLink RvsrLink--wa" href={WA_URL} target="_blank" rel="noopener">
            <i className="icon fab fa-whatsapp" />
            <span className="RvsrLink-body">
              <span className="RvsrLink-title">Join WhatsApp group</span>
              <span className="RvsrLink-sub">Chat &amp; diskusi real-time</span>
            </span>
            <i className="icon fas fa-arrow-up-right-from-square RvsrLink-ext" />
          </a>
          <a className="RvsrLink RvsrLink--gh" href={GITHUB_URL} target="_blank" rel="noopener">
            <i className="icon fab fa-github" />
            <span className="RvsrLink-body">
              <span className="RvsrLink-title">Follow on GitHub</span>
              <span className="RvsrLink-sub">github.com/ReverserID</span>
            </span>
            <i className="icon fas fa-arrow-up-right-from-square RvsrLink-ext" />
          </a>
        </div>
      </section>
      <section className="RvsrWidget">
        <h3 className="RvsrWidget-title">
          Last seen
          {railOnline.items.filter((u) => u.online).length ? <span className="RvsrWidget-count">{railOnline.items.filter((u) => u.online).length} online</span> : null}
        </h3>
        {railOnline.state === 'loading' ? (
          <div className="RvsrWidget-empty">Loading…</div>
        ) : railOnline.items.length ? (
          <div className="RvsrOnline">
            {railOnline.items.map((u) => (
              <Link className={'RvsrOnline-user' + (u.online ? ' is-online' : '')} href={app.route('user', { username: u.slug })} title={u.name}>
                <span className="RvsrOnline-av">
                  <img src={u.avatarUrl || animeAvatarUrl(u.id || u.slug || u.name)} alt="" loading="lazy" />
                  {u.online ? <span className="RvsrOnline-dot" /> : null}
                </span>
                <span className="RvsrOnline-body">
                  <span className="RvsrOnline-name">{u.name}</span>
                  <span className="RvsrOnline-meta">
                    {u.rank && u.rank.icon ? (
                      <span className={'RvsrOnline-rank ' + u.rank.cls}>
                        <img src={'/assets/brand/rank/' + u.rank.icon + '.png'} alt="" loading="lazy" />
                        {u.rank.label}
                      </span>
                    ) : null}
                    <span className="RvsrOnline-seen">{u.online ? 'online now' : humanTime(new Date(u.ts))}</span>
                  </span>
                </span>
              </Link>
            ))}
          </div>
        ) : (
          <div className="RvsrWidget-empty">No members yet.</div>
        )}
      </section>
      {railFiles.items.length || railFiles.state === 'loading' ? (
      <section className="RvsrWidget">
        <h3 className="RvsrWidget-title">Latest files</h3>
        {railFiles.state === 'loading' ? (
          <div className="RvsrWidget-empty">Loading…</div>
        ) : railFiles.items.length ? (
          <ul className="RvsrWidget-list">
            {railFiles.items.map((f) => (
              <li>
                <a className="RvsrWidget-file" href={f.url} target="_blank" rel="noopener">
                  <i className={'icon ' + fileIcon(f.baseName || 'file')} />
                  <span className="RvsrWidget-fileName">{f.baseName || 'file'}</span>
                  <span className="RvsrWidget-fileMeta">
                    {f.humanSize || ''}
                    {f.createdAt ? ' · ' + fmtDate(f.createdAt) : ''}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <div className="RvsrWidget-empty">No files yet.</div>
        )}
      </section>
      ) : null}
      {railRes.items.length || railRes.state === 'loading' ? (
      <section className="RvsrWidget">
        <h3 className="RvsrWidget-title">Latest resources</h3>
        {railRes.state === 'loading' ? (
          <div className="RvsrWidget-empty">Loading…</div>
        ) : railRes.items.length ? (
          <ul className="RvsrWidget-list">
            {railRes.items.map((d) => {
              let when = null;
              try {
                when = d.lastPostedAt && d.lastPostedAt();
              } catch (_) {
                when = null;
              }
              let title = '';
              try {
                title = d.title();
              } catch (_) {
                title = '';
              }
              return (
                <li>
                  <Link className="RvsrWidget-res" href={discussionHref(d)}>
                    <span className="RvsrWidget-resTitle">{title}</span>
                    {when ? <span className="RvsrWidget-resMeta">{humanTime(when)}</span> : null}
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="RvsrWidget-empty">No resources yet.</div>
        )}
      </section>
      ) : null}
    </div>
  );
}

export default function addHomefeed() {
  // Hero: judul + statistik + CTA (Mulai diskusi / WhatsApp / Jelajahi).
  override(WelcomeHero.prototype, 'contentItems', function () {
    const items = new ItemList();
    const title = 'Indonesian Reverse Engineering Community';
    const tags = forumTags();
    const total = tags.reduce((sum, t) => sum + tagDiscussions(t), 0);

    items.add('eyebrow', <div className="RvsrHome-eyebrow">{'// reverser.id'}</div>, 110);
    items.add('title', <h1 className="Hero-title RvsrHome-title">{title}</h1>, 100);
    if (tags.length) {
      items.add(
        'stats',
        <div className="RvsrHome-stats">
          <span>{tags.length} groups</span>
          <i>•</i>
          <span>{total} discussions</span>
        </div>,
        80
      );
    }

    return items;
  });

  // Konten: prompt + strip grup; homepage pakai section per kategori.
  override(IndexPage.prototype, 'contentItems', function (original) {
    const items = original();
    const user = app.session.user;

    items.add(
      'prompt',
      <div className="RvsrPrompt" onclick={startDiscussion} onkeydown={onEnter} role="button" tabindex="0">
        {user ? (
          avatar(user, { className: 'RvsrPrompt-avatar', loading: 'lazy' })
        ) : (
          <span className="RvsrPrompt-avatar RvsrPrompt-guest">
            <i className="icon fas fa-user-astronaut" />
          </span>
        )}
        <span className="RvsrPrompt-fake">{user ? 'Share a finding, question, or write-up…' : 'Log in to join the discussion…'}</span>
        <span className="RvsrPrompt-btn">
          <i className="icon fas fa-pen" /> Post
        </span>
      </div>,
      110
    );

    const popular = forumTags()
      .filter((t) => isValidTag(t) && tagDiscussions(t) > 0)
      .sort((a, b) => tagDiscussions(b) - tagDiscussions(a))
      .slice(0, 8);
    if (popular.length) {
      items.add(
        'groups',
        <div className="RvsrGroups" aria-label="Grup populer">
          {popular.map((t) => (
            <Link className="RvsrGroups-chip" href={tagHref(t)}>
              <span className="RvsrGroups-dot" style={{ '--rv-tag': tagColor(t) }}>
                {tagDot(t)}
              </span>
              {tagName(t)}
              <em>{tagDiscussions(t)}</em>
            </Link>
          ))}
        </div>,
        106
      );
    }

    let path = '';
    try {
      path = m.route.get() || '';
    } catch (_) {
      path = '';
    }
    const isTagPage = path.split('?')[0].indexOf('/t/') === 0;
    // ?sort=newest (dari tombol "See all") → biarin Flarum render discussion
    // list asli (sort + pagination jalan), jangan dirangkap hero sections.
    const isSortList = path.indexOf('?sort=') !== -1 || path.indexOf('&sort=') !== -1;
    if (!isTagPage && !isSortList) {
      items.remove('toolbar');
      items.remove('discussionList');
      items.add('latest', latestSectionView(), 100);
      const secs = sectionTags();
      if (secs.length) {
        items.add('sections', <div className="RvsrSections">{secs.map((t) => sectionView(t))}</div>, 90);
      }
    }

    return items;
  });

  // Layout 3 kolom: nav kiri + konten + rail kanan (widget).
  override(IndexPage.prototype, 'view', function (original) {
    let path = '';
    try {
      path = m.route.get() || '';
    } catch (_) {
      path = '';
    }
    const isTagPage = path.split('?')[0].indexOf('/t/') === 0;
    const isSortList = path.indexOf('?sort=') !== -1 || path.indexOf('&sort=') !== -1;

    // ?sort=newest (tombol "See all"): tampilin discussion list Flarum asli
    // (sort + pagination jalan), tanpa bungkus hero/grid.
    if (isSortList) {
      return (
        <div className="IndexPage">
          {this.hero()}
          <div className="container">
            <div className="sideNavContainer">
              <nav className="IndexPage-nav sideNav">
                <ul>{listItems(this.sidebarItems().toArray())}</ul>
              </nav>
              <div className="IndexPage-results sideNavOffset">{this.contentItems().toArray()}</div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="IndexPage RvsrHome">
        {this.hero()}
        <div className="container">
          <div className={'sideNavContainer' + (isTagPage ? '' : ' RvsrHome-grid')}>
            <nav className="IndexPage-nav sideNav">
              <ul>{listItems(this.sidebarItems().toArray())}</ul>
            </nav>
            <div className="IndexPage-results sideNavOffset">{this.contentItems().toArray()}</div>
            {isTagPage ? null : <aside className="RvsrRail">{railView()}</aside>}
          </div>
        </div>
      </div>
    );
  });
}
